import { config } from '../config';
import { evaluatePlatformGate, gatedVerdict, mergeGates } from './scenario';
import type { EvidenceGate, Verdict } from './scenario';
import { summarize } from './stats';
import type { Summary } from './stats';
import type {
  AssemblyVariant,
  AudioRunRecord,
  ChunkStat,
  FinalizeTrigger,
  GumTrial,
  PlayabilityResult,
  SegmentRecord,
} from '../types';

/**
 * Pure verdict logic for build unit U-1 — assumption **A-2**.
 *
 * A-2, from design §0.1: *"iOS Safari 17 `MediaRecorder` emits fragmented `audio/mp4` and a
 * timesliced chunk sequence concatenated verbatim (init fragment first, chunks in order) is
 * playable by the same browser."* If it is false, AUD-3's partial-segment persistence is
 * unachievable without transcoding, which AUD-1 forbids — so this is not a performance question,
 * it is whether the audio design as written can exist.
 *
 * ── Why this module refuses to answer on a desktop ───────────────────────────
 * Chromium's `MediaRecorder` produces WebM/Opus; iOS produces fragmented MP4/AAC. They have
 * different concatenation semantics, so a green desktop run is not weak evidence for A-2 — it is
 * *no* evidence, about a different container. Every verdict below therefore goes through
 * `gatedVerdict`, which consults `scenario.ts`'s ENV-1 gate **before** looking at any observation.
 * `verify-observable-behavior-not-proxy`: "the harness ran and nothing threw" is a proxy.
 *
 * No DOM, no Dexie, no clock, no randomness. Enforced by `tsconfig.pure.json` and the
 * `nfr-6/pure-layer-boundary` ESLint block.
 */

export type { Verdict } from './scenario';

export interface AudioCheck {
  /** Requirement or assumption id this check answers. */
  id: string;
  label: string;
  /** The question in the words a reader can dispute. */
  question: string;
  verdict: Verdict;
  observed: string;
  expected: string;
  detail: string;
}

/** A fact worth reporting that is not a pass/fail. Findings live here, not in a forced verdict. */
export interface AudioObservation {
  id: string;
  text: string;
}

export interface A2Determination {
  outcome: 'holds' | 'breached' | 'inconclusive';
  headline: string;
  detail: string;
}

export interface AudioReport {
  generatedAt: number;
  gate: EvidenceGate;
  a2: A2Determination;
  checks: AudioCheck[];
  observations: AudioObservation[];
  /** Chunk cadence across every scoreable segment, pooled. Reported as a distribution, never a mean. */
  chunkIntervalMs: Summary | null;
  chunkSizeBytes: Summary | null;
  coldAcquireMs: Summary | null;
  warmTapToRecordingMs: Summary | null;
  /** Segments with enough chunks to say anything about concatenation. */
  scoreableSegments: number;
  totalSegments: number;
  /** Which AUD-3 finalize triggers this run actually exercised. */
  triggersCovered: string[];
  triggersMissing: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Assembly variants — the slicing rule, so it is testable without a Blob
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Which arrival indices each probe variant concatenates, given `count` chunks.
 *
 * - `full` — the sequence as recorded. What AUD-1 stores and playback uses verbatim.
 * - `truncatedTail` — every chunk but the last. This models design §8.5's honest limit exactly:
 *   *"on a hard jetsam kill with no `pagehide`, the chunks written so far survive but the final
 *   partial chunk does not."* If this is unplayable, AUD-3's "captured audio is never discarded"
 *   still holds byte-wise but delivers nothing a user can hear, which is a design consequence, not
 *   a bug to hide.
 * - `firstChunkOnly` — the init fragment alone. Answers "does a one-timeslice flag play at all?",
 *   which is the worst case for a flag stopped almost immediately.
 * - `withoutFirstChunk` — a **negative control**. A-2 asserts the init fragment must come first. If
 *   a sequence missing it plays anyway, that half of A-2 is not a constraint on this browser and
 *   the design gains an option it did not know it had. If it does not play, the ordering
 *   requirement is confirmed rather than assumed.
 *
 * Returns an empty list where the variant cannot be formed (e.g. `truncatedTail` of one chunk),
 * so a caller never probes a zero-byte blob and reads the failure as a container defect.
 */
export function indicesForVariant(count: number, variant: AssemblyVariant): number[] {
  if (count <= 0) return [];
  const all = Array.from({ length: count }, (_, i) => i);
  switch (variant) {
    case 'full':
      return all;
    case 'truncatedTail':
      return count >= 2 ? all.slice(0, count - 1) : [];
    case 'firstChunkOnly':
      return [0];
    case 'withoutFirstChunk':
      return count >= 2 ? all.slice(1) : [];
    default: {
      // Exhaustiveness: a new variant must be handled here, not silently fall through to `full`.
      const never: never = variant;
      throw new Error(`indicesForVariant: unhandled variant ${String(never)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Admissibility (ENV-1 + U-1's own evidence sufficiency)
// ─────────────────────────────────────────────────────────────────────────────

/** A segment carries enough chunks for its playability to say anything about *concatenation*. */
export function isScoreable(segment: SegmentRecord): boolean {
  return segment.complete && segment.chunks.length >= config.AUDIO_SPIKE_MIN_CHUNKS;
}

/**
 * The U-1 gate: `scenario.ts`'s ENV-1 platform gate, plus U-1's own evidence floors.
 *
 * The platform half is not re-implemented here — it is imported. The floors are U-1's, because
 * `empirical-absence-not-invariant`: one segment that happened to play is an anecdote about one
 * recording, not a finding about concatenation.
 */
export function evaluateAudioGate(run: AudioRunRecord): EvidenceGate {
  const blockers: string[] = [];
  const warnings: string[] = [];

  const scoreable = run.segments.filter(isScoreable);

  if (!run.capabilities.mediaRecorderSupported) {
    blockers.push(
      'MediaRecorder is not available in this browser at all, so nothing below is a measurement of it.',
    );
  }
  if (scoreable.length < config.AUDIO_SPIKE_MIN_SEGMENTS) {
    blockers.push(
      `Only ${scoreable.length} segment(s) carried at least ${config.AUDIO_SPIKE_MIN_CHUNKS} chunks; A-2 needs ${config.AUDIO_SPIKE_MIN_SEGMENTS}. One playable assembly is an anecdote about one recording.`,
    );
  }
  if (run.segments.length > scoreable.length) {
    warnings.push(
      `${run.segments.length - scoreable.length} segment(s) were too short (or never finalized) to score, and are reported but excluded from the A-2 verdict.`,
    );
  }
  if (run.gumTrials.filter((t) => t.kind === 'cold' && !t.error).length < config.GUM_TRIALS_MIN) {
    warnings.push(
      `Fewer than ${config.GUM_TRIALS_MIN} successful cold acquisitions: the getUserMedia figure is reported but is not a distribution.`,
    );
  }

  return mergeGates(evaluatePlatformGate(run.environment), {
    admissible: blockers.length === 0,
    blockers,
    warnings,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Derived statistics
// ─────────────────────────────────────────────────────────────────────────────

function probe(segment: SegmentRecord, variant: AssemblyVariant): PlayabilityResult | undefined {
  return segment.probes.find((p) => p.variant === variant);
}

/** All inter-chunk intervals across the given segments. The first chunk of each has none. */
export function pooledChunkIntervals(segments: readonly SegmentRecord[]): number[] {
  const out: number[] = [];
  for (const s of segments) {
    for (const c of s.chunks) {
      if (c.sinceLastMs !== null) out.push(c.sinceLastMs);
    }
  }
  return out;
}

export function pooledChunkSizes(segments: readonly SegmentRecord[]): number[] {
  return segments.flatMap((s: SegmentRecord) => s.chunks.map((c: ChunkStat) => c.sizeBytes));
}

function summarizeOrNull(values: readonly number[]): Summary | null {
  return values.length === 0 ? null : summarize(values);
}

type GumTiming = 'acquireMs' | 'recorderStartMs' | 'tapToRecordingMs';

/** Failed trials are excluded, never coerced to 0 — a denial is not a fast acquisition. */
function gumValues(trials: readonly GumTrial[], kind: GumTrial['kind'], field: GumTiming): number[] {
  return trials
    .filter((t) => t.kind === kind && t.error === null)
    .map((t) => t[field])
    .filter((v) => Number.isFinite(v));
}

// ─────────────────────────────────────────────────────────────────────────────
// Checks
// ─────────────────────────────────────────────────────────────────────────────

const ALL_TRIGGERS = [
  'userStop',
  'plannedDuration',
  'voiceNoteMax',
  'recorderError',
  'trackEnded',
  'pageHide',
  'visibilityHidden',
] as const;

/**
 * The AUD-3 triggers a venue session should actually exercise.
 *
 * `recorderError` is excluded — it cannot be provoked on demand, and a run that never saw one is
 * not a run that skipped a step. The other three are each reachable by a deliberate action, and
 * `trackEnded` is the one that needs a **real phone call**.
 */
const EXPECTED_TRIGGERS = ['userStop', 'trackEnded', 'pageHide'] as const;

function buildChecks(run: AudioRunRecord, admissible: boolean): AudioCheck[] {
  const scoreable = run.segments.filter(isScoreable);
  const checks: AudioCheck[] = [];

  // ── A-2, the crux: does a verbatim concatenation play? ─────────────────────
  const fullPlayable = scoreable.filter((s) => probe(s, 'full')?.canPlayThrough === true);
  checks.push({
    id: 'A-2',
    label: 'Verbatim concatenation is playable',
    question:
      'Assembled with no transcoding, init fragment first, chunks in arrival order — does the browser that recorded it play the result through?',
    verdict: gatedVerdict(scoreable.length > 0 && fullPlayable.length === scoreable.length, admissible),
    observed: `${fullPlayable.length} of ${scoreable.length} scoreable segment(s) played through`,
    expected: 'all of them',
    detail:
      'If this fails, AUD-1’s no-transcoding rule and AUD-3’s partial-segment persistence cannot both hold, and the audio design returns to the stakeholder (design §0.1).',
  });

  // ── AUD-3: is a sequence cut short still playable? ─────────────────────────
  const truncatable = scoreable.filter((s) => probe(s, 'truncatedTail') !== undefined);
  const truncPlayable = truncatable.filter((s) => probe(s, 'truncatedTail')?.canPlayThrough === true);
  checks.push({
    id: 'AUD-3',
    label: 'A truncated sequence is still playable',
    question:
      'Drop the final chunk — design §8.5’s hard-jetsam case. Does what survived still play, or is it bytes nobody can hear?',
    verdict: gatedVerdict(truncatable.length > 0 && truncPlayable.length === truncatable.length, admissible),
    observed: `${truncPlayable.length} of ${truncatable.length} truncated assembly(ies) played through`,
    expected: 'all of them',
    detail:
      'AUD-3 says captured audio is never discarded. If a truncated fragmented-MP4 sequence will not play, "never discarded" is satisfied byte-wise and empty in practice — a finding with real design consequence, and the case §8.2’s playability probe and its "audio unavailable — flagged at 34′" label exist for.',
  });

  // ── AUD-3: every exit routes through finalize, and the recorder is not wedged ──
  const interrupted = run.segments.filter(
    (s) => s.trigger !== null && s.trigger !== 'userStop' && s.trigger !== 'plannedDuration',
  );
  const reuseAttempted = run.segments.filter((s) => s.recorderReusable.attempted);
  const reuseOk = reuseAttempted.filter((s) => s.recorderReusable.succeeded);
  checks.push({
    id: 'AUD-3 (recorder state)',
    label: 'The recorder accepts the next recording after an interruption',
    question:
      'After a phone call, a page hide or a recorder error, does a fresh record attempt succeed?',
    verdict: gatedVerdict(reuseAttempted.length > 0 && reuseOk.length === reuseAttempted.length, admissible),
    observed: `${reuseOk.length} of ${reuseAttempted.length} restart attempt(s) succeeded`,
    expected: 'all of them',
    detail:
      'AUD-3: "the recorder is never left in a state where a subsequent record attempt fails." Design §8.5 buys this by constructing a fresh MediaRecorder per recording; this is the check that it worked.',
  });

  // ── AUD-3: interruption segments kept their audio ──────────────────────────
  const interruptedWithAudio = interrupted.filter((s) => s.totalBytes > 0);
  checks.push({
    id: 'AUD-3 (persistence)',
    label: 'An interrupted recording kept the audio it had captured',
    question: 'When the recording was cut short by something other than a tap, did bytes survive?',
    verdict: gatedVerdict(
      interrupted.length > 0 && interruptedWithAudio.length === interrupted.length,
      admissible,
    ),
    observed:
      interrupted.length === 0
        ? 'no interruption was exercised in this run'
        : `${interruptedWithAudio.length} of ${interrupted.length} interrupted segment(s) held audio`,
    expected: 'all of them, and at least one interruption exercised',
    detail:
      'The interruption set AUD-3 names is: audio interruption (a real phone call), page hide, and a Match.state change. A run that exercised none of them has not tested AUD-3.',
  });

  // ── §8.5: is the timeslice cadence what was asked for? ─────────────────────
  const intervals = pooledChunkIntervals(scoreable);
  const intervalSummary = summarizeOrNull(intervals);
  const lo = config.TIMESLICE_MS * (1 - config.TIMESLICE_TOLERANCE_FRACTION);
  const hi = config.TIMESLICE_MS * (1 + config.TIMESLICE_TOLERANCE_FRACTION);
  const maxAllowed = config.TIMESLICE_MS * config.TIMESLICE_MAX_INTERVAL_FACTOR;
  checks.push({
    id: 'design §8.5',
    label: 'Timeslice chunking is honoured',
    question: `MediaRecorder.start(${config.TIMESLICE_MS}) was requested. Does iOS deliver chunks at that cadence, and is the worst gap bounded?`,
    verdict: gatedVerdict(
      intervalSummary !== null &&
        intervalSummary.p50 >= lo &&
        intervalSummary.p50 <= hi &&
        intervalSummary.max <= maxAllowed,
      admissible,
    ),
    observed:
      intervalSummary === null
        ? 'no inter-chunk intervals were recorded'
        : `median ${Math.round(intervalSummary.p50)} ms, worst ${Math.round(intervalSummary.max)} ms over ${intervalSummary.n} interval(s)`,
    expected: `median within [${Math.round(lo)}, ${Math.round(hi)}] ms and worst ≤ ${Math.round(maxAllowed)} ms`,
    detail:
      'Design §8.5 states "worst-case loss is one timeslice, not the whole segment". That sentence is only true if the cadence is bounded — the worst observed gap is the real bound on how much a hard jetsam kill costs.',
  });

  // ── FLAG-1: warm start, the number that decides whether words are lost ─────
  const warmValues = gumValues(run.gumTrials, 'warm', 'tapToRecordingMs');
  const warmSummary = summarizeOrNull(warmValues);
  checks.push({
    id: 'FLAG-1',
    label: 'Tap to recording, with the stream already held',
    question:
      'Design §8.1 holds the microphone stream across in-match states so a Flag tap starts recording immediately. With it held, how long from tap to recording?',
    verdict: gatedVerdict(
      warmSummary !== null && warmSummary.p95 <= config.TAP_ACK_BUDGET_MS,
      admissible,
    ),
    observed:
      warmSummary === null
        ? 'no warm trials'
        : `p50 ${Math.round(warmSummary.p50)} ms, p95 ${Math.round(warmSummary.p95)} ms, max ${Math.round(warmSummary.max)} ms`,
    expected: `p95 ≤ ${config.TAP_ACK_BUDGET_MS} ms (NFR-1’s budget — FLAG-1 promises the same "immediately" every other tap promises)`,
    detail:
      'FLAG-1: "single tap starts recording immediately with no confirmation". Every millisecond here is opening words of a flag that are not captured — which is the part that says what happened.',
  });

  // ── §8.1: the cold acquisition the design rejected per-recording capture over ──
  const coldValues = gumValues(run.gumTrials, 'cold', 'acquireMs');
  const coldSummary = summarizeOrNull(coldValues);
  checks.push({
    id: 'design §8.1',
    label: 'Cold getUserMedia acquisition cost',
    question:
      'How long does acquiring the microphone stream actually take on this device, with permission already granted?',
    verdict: gatedVerdict(
      coldSummary !== null && coldSummary.p95 <= config.GUM_COLD_ASSUMED_MAX_MS,
      admissible,
    ),
    observed:
      coldSummary === null
        ? 'no successful cold acquisitions'
        : `p50 ${Math.round(coldSummary.p50)} ms, p95 ${Math.round(coldSummary.p95)} ms, max ${Math.round(coldSummary.max)} ms over ${coldSummary.n}`,
    expected: `p95 ≤ ${config.GUM_COLD_ASSUMED_MAX_MS} ms — the top of §8.1’s own stated "200–800 ms"`,
    detail:
      '§8.1 rejects per-recording acquisition on exactly this number and holds the stream for the whole match instead, paying an always-lit iOS recording indicator and some battery for it. If acquisition is actually cheap, that trade is worth revisiting; if it is worse than 800 ms, the long-lived stream is more load-bearing than the design claims.',
  });

  return checks;
}

function buildObservations(run: AudioRunRecord): AudioObservation[] {
  const out: AudioObservation[] = [];
  const scoreable = run.segments.filter(isScoreable);

  // AUD-1 — what the recorder actually emitted. Reported, never assumed.
  const mimes = [...new Set(run.segments.map((s) => s.actualMime).filter((m): m is string => !!m))];
  out.push({
    id: 'AUD-1 mime',
    text:
      mimes.length === 0
        ? 'No recording produced a MIME type. AUD-1 requires the blob be stored with the type the recorder emitted; there is nothing to store.'
        : `MediaRecorder.mimeType on this device: ${mimes.join(', ')}. Default with no options requested: ${run.capabilities.defaultMime ?? '—'}. AUD-1 stores this verbatim.`,
  });

  const chunkTypes = [...new Set(scoreable.flatMap((s) => s.chunks.map((c) => c.type)))];
  out.push({
    id: 'AUD-1 chunk types',
    text: `Blob.type on the individual chunks: ${chunkTypes.map((t) => (t === '' ? '(empty)' : t)).join(', ') || '—'}. An empty type on chunks after the first is normal and is why the segment mime comes from the recorder, not from a chunk.`,
  });

  // The init-fragment negative control.
  const controls = scoreable
    .map((s) => probe(s, 'withoutFirstChunk'))
    .filter((p): p is PlayabilityResult => p !== undefined);
  if (controls.length > 0) {
    const playedWithoutInit = controls.filter((p) => p.canPlayThrough).length;
    out.push({
      id: 'A-2 init fragment',
      text:
        playedWithoutInit === 0
          ? `Negative control: ${controls.length} assembly(ies) with the first chunk removed were all unplayable. The init fragment is load-bearing, exactly as A-2 assumes — chunk order is a real constraint, not a precaution.`
          : `Negative control: ${playedWithoutInit} of ${controls.length} assemblies played WITHOUT the first chunk. On this device the init fragment is not strictly required, which is a wider option than A-2 assumed — worth telling the design agent before U-11.`,
    });
  }

  const firstOnly = scoreable
    .map((s) => probe(s, 'firstChunkOnly'))
    .filter((p): p is PlayabilityResult => p !== undefined);
  if (firstOnly.length > 0) {
    const ok = firstOnly.filter((p) => p.canPlayThrough).length;
    out.push({
      id: 'flag stopped instantly',
      text: `A single-chunk assembly (a flag stopped almost immediately) played through in ${ok} of ${firstOnly.length} case(s). This is the shortest flag the product can produce.`,
    });
  }

  // A playable assembly with no duration. Verified against a real browser during U-1: a timesliced
  // container carries no total duration in its header (it was still being written), so the element
  // reports Infinity or NaN. Called out because "duration —" next to "plays" looks like a defect
  // and is not — and because it has a real product consequence for U-11's playback UI.
  const playableNoDuration = scoreable
    .map((s) => probe(s, 'full'))
    .filter((p): p is PlayabilityResult => !!p && p.canPlayThrough && p.durationSeconds === null);
  if (playableNoDuration.length > 0) {
    out.push({
      id: 'duration unavailable',
      text: `${playableNoDuration.length} assembly(ies) played through but reported no duration. That is normal for a timesliced container — the total length was never written into the header — and is NOT a sign of a broken file. U-11's playback UI cannot show a progress bar or a "0:14" label from the blob alone; it must derive length from the recording's own wall clock.`,
    });
  }

  // The metadata-vs-playthrough distinction, which is where a false negative would hide.
  const metadataOnly = scoreable
    .map((s) => probe(s, 'full'))
    .filter((p): p is PlayabilityResult => !!p && !p.canPlayThrough && p.metadataLoaded);
  if (metadataOnly.length > 0) {
    out.push({
      id: 'probe nuance',
      text: `${metadataOnly.length} full assembly(ies) parsed far enough to yield metadata but did not report canplaythrough. That is "the container is readable, the browser would not commit to playing it" — press Play on the segment and trust your ears over the probe.`,
    });
  }

  // The human tiebreak, and any contradiction between it and the probe.
  const heard = run.segments.filter((s) => s.manualVerdict === 'played').length;
  const contradictions = run.segments.filter(
    (s) => s.manualVerdict === 'silent' && probe(s, 'full')?.canPlayThrough === true,
  ).length;
  out.push({
    id: 'manual playback',
    text:
      `Confirmed by ear: ${heard} of ${run.segments.length} segment(s).` +
      (contradictions > 0
        ? ` ${contradictions} segment(s) the probe called playable were reported SILENT by ear — the probe is wrong about those, and AUD-1's contract is what a person hears.`
        : ''),
  });

  const persistErrors = run.segments.flatMap((s) => s.chunks.filter((c) => c.persistError !== null));
  if (persistErrors.length > 0) {
    out.push({
      id: 'chunk persistence',
      text: `${persistErrors.length} chunk(s) arrived but could not be written to IndexedDB. Those are holes in a recording; the affected segments cannot be used for A-2.`,
    });
  }

  return out;
}

/**
 * The A-2 answer, determined from the measurements.
 *
 * Mirrors `determineF9` in `scenario.ts` deliberately: same three outcomes, same rule that an
 * inadmissible run yields `inconclusive` and says why, so the two harnesses read the same way on a
 * phone in the sun.
 */
export function determineA2(gate: EvidenceGate, checks: readonly AudioCheck[]): A2Determination {
  if (!gate.admissible) {
    return {
      outcome: 'inconclusive',
      headline: 'A-2 undetermined — this run is not evidence',
      detail:
        'Chromium emits WebM/Opus and iOS emits fragmented MP4/AAC, so a desktop pass says nothing about the container A-2 is about. Install this to the home screen on the target iPhone, grant the microphone, and record at least ' +
        `${config.AUDIO_SPIKE_MIN_SEGMENTS} segments of ${config.AUDIO_SPIKE_SEGMENT_S}s. Blockers: ${gate.blockers.join(' · ') || 'none listed'}`,
    };
  }

  const core = checks.filter((c) => c.id === 'A-2' || c.id === 'AUD-3');
  const failed = core.filter((c) => c.verdict === 'fail');

  if (failed.length === 0) {
    const others = checks.filter((c) => c.verdict === 'fail');
    return {
      outcome: 'holds',
      headline:
        others.length === 0
          ? 'A-2 HOLDS — verbatim concatenation and truncated playback both work on this device'
          : `A-2 holds, but ${others.length} secondary check(s) failed: ${others.map((c) => c.id).join(', ')}`,
      detail:
        'The chunk sequence concatenated with no transcoding plays through, and so does the sequence with its final chunk removed. AUD-1’s no-transcoding rule and AUD-3’s partial-segment persistence can both be satisfied by the design as written; U-11 can be built against §8.2 unchanged.' +
        (others.length === 0
          ? ''
          : ' The secondary failures are budget or cadence findings, not container findings — read them before setting U-11’s constants.'),
    };
  }

  const a2Failed = failed.some((c) => c.id === 'A-2');
  return {
    outcome: 'breached',
    headline: a2Failed
      ? 'A-2 IS FALSE on this device — verbatim concatenation does not play'
      : 'A-2 partially false — the full assembly plays but a truncated one does not',
    detail: a2Failed
      ? 'Design §0.1 states the consequence: "AUD-3’s partial-segment persistence is unachievable without transcoding, which AUD-1 forbids." This is a stakeholder decision about the audio contract, not an implementation workaround. Take this report to Andrew before U-11 is built.'
      : 'AUD-3 requires that on interruption the partial segment is finalized, persisted and never discarded. It is being persisted and it is unplayable, so the bytes are kept and nothing can be heard. §8.2’s playability probe and its "audio unavailable — flagged at 34′" label already anticipate this, but the size of the loss — up to one whole recording on a hard kill — is a decision for the stakeholder.',
  };
}

export function buildAudioReport(run: AudioRunRecord, generatedAt: number): AudioReport {
  const gate = evaluateAudioGate(run);
  const checks = buildChecks(run, gate.admissible);
  const scoreable = run.segments.filter(isScoreable);

  const seen = new Set<string>(
    run.segments.map((s) => s.trigger).filter((t): t is FinalizeTrigger => t !== null),
  );

  return {
    generatedAt,
    gate,
    a2: determineA2(gate, checks),
    checks,
    observations: buildObservations(run),
    chunkIntervalMs: summarizeOrNull(pooledChunkIntervals(scoreable)),
    chunkSizeBytes: summarizeOrNull(pooledChunkSizes(scoreable)),
    coldAcquireMs: summarizeOrNull(gumValues(run.gumTrials, 'cold', 'acquireMs')),
    warmTapToRecordingMs: summarizeOrNull(gumValues(run.gumTrials, 'warm', 'tapToRecordingMs')),
    scoreableSegments: scoreable.length,
    totalSegments: run.segments.length,
    triggersCovered: ALL_TRIGGERS.filter((t) => seen.has(t)),
    triggersMissing: EXPECTED_TRIGGERS.filter((t) => !seen.has(t)),
  };
}

/** The one-line answer for the top of the screen — legible at arm's length, outdoors. */
export function audioHeadline(report: AudioReport): string {
  if (!report.gate.admissible) return 'NOT EVIDENCE — see ENV-1 blockers below';
  return report.a2.headline;
}
